import { useEffect, useState } from 'react';

import type { RetryInfo } from '../groupCreation';

// Shown beneath a pending creation step that is waiting out a Graph replication/throttle
// backoff (see addWithRetry). Without this the step is just a spinner that looks hung;
// here the user sees why we're waiting, which attempt we're on, and how long is left.
//
// The countdown is local and self-contained: it ticks from delayMs down to 0 off
// performance.now(). Re-keying on `attempt` (in the parent) remounts it each retry so the
// bar and timer reset cleanly for the next, longer wait.
const TICK_MS = 100;

export default function RetryCountdown({ attempt, total, delayMs }: RetryInfo) {
  const [remainingMs, setRemainingMs] = useState(delayMs);

  useEffect(() => {
    const start = performance.now();
    const id = setInterval(() => {
      const left = Math.max(0, delayMs - (performance.now() - start));
      setRemainingMs(left);
      if (left <= 0) clearInterval(id);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [delayMs]);

  const secondsLeft = Math.ceil(remainingMs / 1000);
  const when = secondsLeft > 0 ? `retrying in ${secondsLeft}s` : 'retrying…';

  return (
    <div className="text-base-content/70 text-xs mt-1">
      {/* Single text node (not interpolated spans) so the whole sentence is one match. */}
      <div>{`Waiting for directory replication — ${when} (attempt ${attempt} of ${total})`}</div>
      <progress
        className="progress progress-primary w-40 h-1 mt-1"
        value={delayMs - remainingMs}
        max={delayMs}
      />
    </div>
  );
}
